import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import FeedbackSidebar from "../../../widgets/FeedbackSidebar"; // shared component

function getScoreLevel(score) {
  const n = typeof score === "number" ? score : parseFloat(score);
  if (Number.isNaN(n)) return "na";
  if (n >= 80) return "good";
  if (n >= 60) return "avg";
  return "low";
}

// ───────────────────────────────
// Single feedback card component
// ───────────────────────────────
const FeedbackCard = ({ item, fileUrl, getFileName, onPlay, onViewMore }) => {
  const traineeName = item.traineeName || item.traineeId?.name || "-";
  const traineeEmail = item.traineeEmail || item.traineeId?.email || "-";
  const callType = item.callType || "-";
  const feedbackText = item.feedback || "-";
  const score = item.score ?? "-";
  const date = item.createddate
    ? new Date(item.createddate).toLocaleString()
    : "-";
    const callId = item.feedbackId || item.callId || "-";

  const scoreLevel = getScoreLevel(score);
  const canPlay =
    item.callRecordFileinfo &&
    (item.artifactTranscriptFilePath || item.transcriberJsonFilePath);

  const transcriberHref = item.transcriberFilePath
    ? fileUrl(item.transcriberFilePath)
    : null;
  const transcriberTitle = item.transcriberFilePath
    ? getFileName(item.transcriberFilePath)
    : "";

  const callRecordHref = item.callRecordFileinfo
    ? fileUrl(item.callRecordFileinfo)
    : null;
  const callRecordTitle = item.callRecordFileinfo
    ? getFileName(item.callRecordFileinfo)
    : "";

  // ✅ legal hook usage
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 2);
    }
  }, [feedbackText]);

  return (
    <article key={item._id} className="fb-card fb-card--compact">
      {/* Row 1: identity + score */}
      <div className="fb-row fb-row--top">
        <div className="fb-name-email">
          <div className="fb-name" title={traineeName}>
            {traineeName}
          </div>
          <div className="fb-email" title={traineeEmail}>
            {traineeEmail}
          </div>
        </div>
        <div
          className={`fb-score fb-score--${scoreLevel}`}
          title={`Score: ${score}`}
        >
          <span className="fb-score__value">{score}/10</span>
        </div>
      </div>

      {/* Row 2: meta chips */}
      <div className="fb-row fb-row--meta">
        <span className="chip" title="Call Type">
          {callType}
        </span>
        <span className="chip chip--muted" title="Date">
          {date}
        </span>
        <span className="chip chip--info" title="Call ID">
          ID: {callId}
        </span>
      </div>

      {/* Row 3: feedback text */}
      <div className="fb-row fb-row--feedback" style={{ marginBottom: 45 }}>
        <p
          ref={textRef}
          className="fb-text"
        >
          {feedbackText}
        </p>

        {isOverflowing && (
          <button
            className="btn btn-secondary"
            style={{
              fontSize: "13px",
              padding: "6px 10px",
              marginTop: "8px",
              borderRadius: "8px",
              textWrap: "nowrap",
            }}
            onClick={() => onViewMore(item)}
          >
            View more
          </button>
        )}
      </div>

      {/* Row 4: play button */}
      <div className="fb-action">
        <button
          className="btn btn-play"
          onClick={() => onPlay && onPlay(item)}
          disabled={!canPlay}
          aria-disabled={!canPlay}
          title={canPlay ? "Play & Review" : "Unavailable"}
        >
          <Play size={18} />
          <span className="btn-play__text">Play recording</span>
        </button>
      </div>
    </article>
  );
};

// ───────────────────────────────
// Main table wrapper
// ───────────────────────────────
const FeedbackTable = ({
  feedback,
  items: itemsProp,
  loading = false,
  baseApiUrl,
  getFileName,
  onPlay,
}) => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const items = itemsProp ?? feedback ?? [];

  const fileUrl = (p) =>
    `${baseApiUrl || process.env.REACT_APP_BASE_API_URL}/api/calls/files?path=${encodeURIComponent(
      p
    )}`;

  if (loading) {
    return (
      <div className="fb-list">
        <div className="fb-empty">Loading feedback...</div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="fb-list">
        <div className="fb-empty">No feedback found.</div>
      </div>
    );
  }

  return (
    <>
      <div className="fb-list">
        {items.map((item) => (
          <FeedbackCard
            key={item._id}
            item={item}
            fileUrl={fileUrl}
            getFileName={getFileName}
            onPlay={onPlay}
            onViewMore={setSelectedFeedback}
          />
        ))}
      </div>

      {/* Sidebar for expanded feedback */}
      {selectedFeedback && (
        <FeedbackSidebar
          item={selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
        />
      )}
    </>
  );
};

export default FeedbackTable;
